import { v4 } from 'uuid';

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import * as _ from 'lodash';
import { DateObjectUnits, DateTime } from 'luxon';
import { Calendar, CalendarTypeView } from 'primengv15/calendar';

import format from 'date-fns/format';
import {
  FORM_VALIDATION_ERROR_MSG_MAP,
  convertTimezoneToLocal,
  isNullOrEmpty,
} from '../../../core/public-api';
import { BaseComponent } from '../base.component';

export enum CalenderViewEnum {
  month = 'month',
  date = 'date',
}

// 是否點擊日曆外
Calendar.prototype.isOutsideClicked = function (event: Event) {
  let isInside = false;
  if (this.overlay) {
    const overlayPanelId = this.overlay.dataset['overlayPanelId'];
    const targetPanelId = (
      (event.target as HTMLElement).firstElementChild as HTMLElement
    )?.dataset['overlayPanelId'];
    isInside = overlayPanelId === targetPanelId;
  }
  return !(
    this.el.nativeElement.isSameNode(event.target) ||
    this.isNavIconClicked(event) ||
    this.el.nativeElement.contains(event.target) ||
    (this.overlay && (this.overlay.contains(<Node>event.target) || isInside))
  );
};

// Override p-calendar 輸入框輸入 function
Calendar.prototype.onUserInput = function (event) {
  // IE 11 Workaround for input placeholder : https://github.com/primefaces/primeng/issues/2026
  if (!this.isKeydown) {
    return;
  }
  this.isKeydown = false;

  let val = event.target.value;
  try {
    let value = this.parseValueFromString(val);
    this.updateModel(value);
    this.updateUI();
  } catch (err) {
    //invalid date
    this.updateModel(null);
  }

  this.filled = val != null && val.length;
  this.onInput.emit(event);
};

// Override p-calendar 更新時間 function
Calendar.prototype.updateTime = function () {
  let value = this.value;
  if (this.isRangeSelection()) {
    value = this.value[1] || this.value[0];
  }
  if (this.isMultipleSelection()) {
    value = this.value[this.value.length - 1];
  }

  // 註:只調整這一段，如果 value 是空的 且有設定 defaultDate 就拿 defaultDate 當初始值
  value = value
    ? new Date(value.getTime())
    : this.defaultDate
    ? new Date(this.defaultDate.getTime())
    : new Date();

  if (this.hourFormat == '12') {
    if (this.currentHour === 12) value.setHours(this.pm ? 12 : 0);
    else value.setHours(this.pm ? this.currentHour + 12 : this.currentHour);
  } else {
    value.setHours(this.currentHour);
  }

  value.setMinutes(this.currentMinute);
  value.setSeconds(this.currentSecond);
  if (this.isRangeSelection()) {
    if (this.value[1]) value = [this.value[0], value];
    else value = [value, null];
  }

  if (this.isMultipleSelection()) {
    value = [...this.value.slice(0, -1), value];
  }

  this.updateModel(value);
  this.onSelect.emit(value);
  this.updateInputfield();
};

export const DATE_PICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatePickerComponent),
  multi: true,
};

const DAY_STRART_CONFIG: DateObjectUnits = {
  hour: 0,
  minute: 0,
  second: 0,
  millisecond: 0,
};

/**
 * 日期元件
 *
 * @export
 * @class DatePickerComponent
 * @extends {BaseComponent}
 * @implements {OnInit}
 * @implements {OnChanges}
 * @implements {AfterViewInit}
 * @implements {ControlValueAccessor}
 */
@Component({
  selector: 'sigv-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [DATE_PICKER_VALUE_ACCESSOR],
})
export class DatePickerComponent
  extends BaseComponent
  implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor
{
  private _control: AbstractControl | null = null;
  private inputMask: any;
  readonly validationMsgMap = FORM_VALIDATION_ERROR_MSG_MAP;

  @Input() fieldName: string = '';

  /**
   * 只可選取
   */
  @Input() readonlyInput = false;

  /**
   * 移動元件至
   */
  @Input() appendTo: any;

  /**
   * 模式
   */
  @Input() selectionMode = 'single';

  /**
   * 日期格式
   */
  @Input() dateFormat?: string;

  /**
   * Placeholder
   */
  @Input() placeholder = '';

  /**
   * Disable
   */
  @Input() disabled = false;

  /**
   * 最小值
   */
  @Input() minDate?: Date;

  /**
   * 最大值
   */
  @Input() maxDate?: Date;

  /**
   * 年份下拉範圍
   */
  @Input() yearRange?: string;

  /**
   * 顯示時間
   */
  @Input() showTime = false;

  /**
   * 分鐘 Tick
   */
  @Input() stepMinute = 1;

  /**
   * 顯示時間模式
   */
  @Input() timeOnly = false;

  /**
   * calendar 檢視格式: date, month
   */
  @Input() view: CalendarTypeView = 'date';

  /**
   * calendar model 預設的日期(today)
   * 若有設 minDate 則預設為 minDate
   */
  @Input() defaultDate!: Date;

  /**
   * FormControllName
   */
  @Input() formControlName?: string;

  /**
   * Focus 事件
   */
  @Output() focused: EventEmitter<any> = new EventEmitter();

  /**
   * Blur 事件
   */
  @Output() blured: EventEmitter<any> = new EventEmitter();

  /**
   * Clear 事件
   */
  @Output() cleared: EventEmitter<any> = new EventEmitter();

  /**
   * 選取事件
   */
  @Output() selected: EventEmitter<any> = new EventEmitter();

  /**
   * 輸入事件
   */
  @Output() inputed: EventEmitter<any> = new EventEmitter();
  @ViewChild(Calendar) calendar!: Calendar;
  now = DateTime.now().toJSDate();
  hideOnDateTimeSelect = true;
  primeDateFormat: any;
  maskFormat = '99/99/9999';
  model: any;
  translateParams?: { [key: string]: string | undefined };
  overlayPanelId = v4();
  onModelChange = (event: any) => {};
  onModelTouched = () => {};

  constructor(
    private injector: Injector,
    private el: ElementRef,
    private cd: ChangeDetectorRef
  ) {
    super(injector);
  }

  get control(): AbstractControl | null {
    const ngControlDirective = this.injector.get(NgControl);
    const control = ngControlDirective.control as any | null;
    if (control) {
      const validators: ValidatorFn[] = Array.isArray(control._rawValidators)
        ? control._rawValidators
        : !control._rawValidators
        ? []
        : [control._rawValidators];
      if (validators.indexOf(DateValidators.validDateFormat) < 0) {
        control.setValidators([...validators, DateValidators.validDateFormat]);
        control.updateValueAndValidity();
      }
      this._control = control;
    }

    return this._control;
  }

  ngOnInit(): void {
    this.logger.debug('datePicker', this.envConfig);
    const displayName =
      this.fieldName === ''
        ? ''
        : this.translateService.instant(this.fieldName);
    this.translateParams = { field: displayName };

    // 年區間預設值
    if (isNullOrEmpty(this.yearRange)) {
      this.yearRange = `${this.now.getFullYear() - 5}:${
        this.now.getFullYear() + 5
      }`;
    }

    // 日期格式預設值
    if (this.timeOnly) {
      this.primeDateFormat = null;
    } else {
      if (isNullOrEmpty(this.dateFormat)) {
        this.primeDateFormat =
          this.view === CalenderViewEnum.month
            ? this.transformToPrimeFormat(this.envConfig.format.dateYM)
            : this.transformToPrimeFormat(this.envConfig.format.date);
      } else {
        this.primeDateFormat = this.dateFormat!;
      }
    }

    // 計算遮罩
    this.maskFormat = this.timeOnly
      ? '99:99'
      : this.showTime
      ? `${this.toMaskFormat(
          this.transformToNgFormat(this.primeDateFormat!)
        )} 99:99`
      : this.toMaskFormat(this.transformToNgFormat(this.primeDateFormat!));

    // 計算 placeholder
    this.placeholder = this.timeOnly
      ? 'hh:mm'
      : this.showTime
      ? `${this.transformToNgFormat(this.primeDateFormat!).toLowerCase()} hh:mm`
      : this.transformToNgFormat(this.primeDateFormat!).toLowerCase();

    this.hideOnDateTimeSelect = !this.timeOnly && this.showTime ? false : true;

    this.translateService.onLangChange.subscribe({
      next: (lang: any) => {
        const displayName =
          this.fieldName === ''
            ? ''
            : this.translateService.instant(this.fieldName);
        this.translateParams = { field: displayName };
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (isNullOrEmpty(this.calendar) === false) {
      this.runtimeSetup();
    }
  }

  ngAfterViewInit(): void {
    const inputElement = this.el.nativeElement.querySelector('input');
    if (this.maskFormat && inputElement) {
      const options = {
        oncomplete: () => {
          this.onInput();
        },
      };
      this.inputMask = new Inputmask(this.maskFormat, options);
      this.inputMask.mask(inputElement);
    }

    this.calendar.inputfieldViewChild.nativeElement.addEventListener(
      'paste',
      (event: any) => {
        this.onInput();
      }
    );

    this.calendar.inputfieldViewChild.nativeElement.addEventListener(
      'cut',
      (event: any) => {
        this.onInput();
      }
    );

    this.runtimeSetup();
    this.updateCalendarValue(this.model, true);
    this.cd.detectChanges();
  }

  writeValue(obj: any): void {
    this.model = obj;
    const updateText = isNullOrEmpty(obj)
      ? true
      : DateTime.fromJSDate(obj).isValid;
    this.updateCalendarValue(obj, updateText);
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onModelTouched = fn;
  }

  /**
   * 選取日期
   * p-calendar onSelect event
   * @param {*} event
   * @memberof DatePickerComponent
   */
  onSelect(event: any): void {
    const date = convertTimezoneToLocal(event, false);
    this.updateModel(date, true);

    this.selected.emit(date);
  }

  /**
   * 日期 Blur
   * p-calendar onBlur event
   * @param {*} event
   * @memberof DatePickerComponent
   */
  onBlur(event: any): void {
    let date: Date;
    const inputElement = this.el.nativeElement.querySelector('input');
    try {
      const parsedDate = this.calendar.parseValueFromString(
        inputElement.value
      ) as Date;
      date = convertTimezoneToLocal(parsedDate, false);
    } catch (e) {
      inputElement.value = '';
    }
    this.blured.emit(event);
  }
  /**
   * 日期 Clear
   * p-calendar onBlur event
   * @param {*} event
   * @memberof DatePickerComponent
   */
  onClear(event: any): void {
    this.cleared.emit(event);
  }
  /**
   * 日期 Focus
   * p-calendar onFocus event
   * @param {*} event
   * @memberof DatePickerComponent
   */
  onFocus(event: any): void {
    this.onModelTouched();
    this.focused.emit(event);
  }

  /**
   * 日期 Input
   * p-calendar onInput event
   * @param {*} event
   * @memberof DatePickerComponent
   */
  async onInput(): Promise<void> {
    let date: Date;
    try {
      const inputElement = this.el.nativeElement.querySelector('input');
      const parsedDate = this.calendar.parseValueFromString(
        inputElement.value
      ) as Date;
      date = convertTimezoneToLocal(parsedDate, false);
    } catch (e) {
      date = new Date('');
    }

    if (this.model?.valueOf() === date?.valueOf()) {
      return;
    }

    this.updateModel(date, false);
    await this.control?.updateValueAndValidity();
    this.inputed.emit(event);
  }

  /**
   * 日曆顯示事件
   *
   * @param {*} event
   * @memberof DatePickerComponent
   */
  onShow(event: any): void {
    const overlay = event.element as HTMLElement;
    if (overlay) {
      overlay.dataset['overlayPanelId'] = this.overlayPanelId;
    }
  }

  /**
   * 日期時間模式下，OK 按鈕 click 事件
   *
   * @memberof DatePickerComponent
   */
  onPanelOKBtnClick(): void {
    this.calendar.toggle();
  }

  private updateModel(event: any, isUpdateText: boolean): void {
    let dateVal = event;

    // 日期模式要將時間歸0;時間模式的秒與毫秒要歸0
    let date = event as Date;
    if (isNullOrEmpty(date) === false && DateTime.fromJSDate(date).isValid) {
      date =
        this.showTime || this.timeOnly
          ? DateTime.fromJSDate(date)
              .set({ second: 0, millisecond: 0 })
              .toJSDate()
          : DateTime.fromJSDate(date).set(DAY_STRART_CONFIG).toJSDate();
    }
    dateVal = date;

    this.model = dateVal;
    if (DateTime.fromJSDate(dateVal).isValid) {
      this.updateCalendarValue(dateVal, isUpdateText);
    } else {
      // 如果日期 Invalid 仍要更新區間設定到預設值
      this.runtimeSetup();
    }

    this.onModelChange(dateVal);
  }

  /**
   * 轉換 Angular 時間格式至 Primeng 時間格式
   *
   * @private
   * @param {string} format Angular 時間格式
   * @return {*}  {string}
   * @memberof DatePickerComponent
   */
  private transformToPrimeFormat(format: string): string {
    let primeFormat = '';
    let regex = /yyyy/;
    primeFormat = format.replace(regex, 'yy');
    regex = /MM/;
    primeFormat = primeFormat.replace(regex, 'mm');
    return primeFormat;
  }

  private transformToNgFormat(format: string): string {
    let ngFormat = '';
    let regex = /yy/;
    ngFormat = format.replace(regex, 'yyyy');
    regex = /mm/;
    ngFormat = ngFormat.replace(regex, 'MM');
    return ngFormat;
  }

  /**
   * 設置區間設定
   *
   * @param {*} [value]
   * @memberof DatePickerComponent
   */
  runtimeSetup(value?: any): void {
    this.calendar.maxDate = this.maxDate ?? new Date(8640000000000000);
    this.calendar.minDate = this.minDate ?? new Date(-8640000000000000);

    // 根據輸入的日期去調整可選擇的下拉年份
    let updateYearRange = this.yearRange;
    if (!isNullOrEmpty(value)) {
      const selectedYear = value.getFullYear();
      const yearRangeStrArray = this.yearRange?.split(':') ?? [];
      if (yearRangeStrArray.length === 2) {
        const startYear = DateTime.fromObject({
          year: Number(_.first(yearRangeStrArray)),
        }).year;
        const endYear = DateTime.fromObject({
          year: Number(_.last(yearRangeStrArray)),
        }).year;
        if (selectedYear - 5 < startYear) {
          updateYearRange = `${selectedYear - 5}:${endYear}`;
        } else if (selectedYear + 5 > endYear) {
          updateYearRange = `${startYear}:${selectedYear + 5}`;
        }
      }
    }

    if (isNullOrEmpty(updateYearRange) === false) {
      this.calendar.yearRange = updateYearRange!;
    }
    this.calendar.updateUI();
  }

  private toMaskFormat(format: string): string {
    const regex = /[yYmMdDhH]/g;
    return format.toLowerCase().replace(':ss', '').replace(regex, '9');
  }

  /**
   * 更新 p-calendar 值
   *
   * @private
   * @param {*} value
   * @memberof DatePickerComponent
   */
  private updateCalendarValue(value: any, isUpdateText: boolean): void {
    if (isNullOrEmpty(this.calendar) === false) {
      this.calendar.value = DateTime.fromJSDate(value).isValid
        ? convertTimezoneToLocal(value, true)
        : null;
      if (isUpdateText) {
        this.calendar.updateInputfield();
      }
      this.runtimeSetup(value);
    }
  }
}

/**
 * 日期檢核
 *
 * @export
 * @class DateValidators
 */
export class DateValidators {
  static validDateFormat(ctrl: AbstractControl): ValidationErrors | null {
    if (ctrl.value == 'Invalid Date') {
      return { invalidDate: true };
    }
    return null;
  }
  /**
   *  Validator that requires the control's value to be less than (or equal) to the provided date.
   *
   * @static
   * @param {Date} max
   * @param {boolean} [orEqual=true]
   * @return {*}  {(ValidatorFn | null)}
   * @memberof ValidatorsPlus
   */
  static maxDateComp(max: Date, orEqual = true): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!ctrl) {
        return null;
      }

      const maxDateTime = DateTime.fromJSDate(max);
      const targetDateTime = DateTime.fromJSDate(ctrl.value);

      if (!maxDateTime.isValid) {
        throw 'Invalid max date.';
      }

      if (!targetDateTime.isValid) {
        return null;
      }

      const maxDate = maxDateTime.startOf('day');
      const targetDate = targetDateTime.startOf('day');

      const result = (orEqual ? targetDate > maxDate : targetDate >= maxDate)
        ? {
            maxDateComp: {
              maxDate: format(
                max,
                globalThis.sigv.appContext.envConfig.format.date
              ),
              orEqual,
            },
          }
        : null;

      return result;
    };
  }

  /**
   *  Validator that requires the control's value to be greater than (or equal) to the provided date.
   *
   * @static
   * @param {Date} min
   * @param {boolean} [orEqual=true]
   * @return {*}  {(ValidatorFn | null)}
   * @memberof ValidatorsPlus
   */
  static minDateComp(min: Date, orEqual = true, custMsg?: string): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!ctrl) {
        return null;
      }

      const minDateTime = DateTime.fromJSDate(min);
      const targetDateTime = DateTime.fromJSDate(ctrl.value);

      if (!minDateTime.isValid) {
        if (!isNullOrEmpty(custMsg)) throw custMsg;
        throw 'Invalid min date.';
      }

      if (!targetDateTime.isValid) {
        return null;
      }

      const minDate = minDateTime.startOf('day');
      const targetDate = targetDateTime.startOf('day');

      const result = (orEqual ? targetDate < minDate : targetDate <= minDate)
        ? {
            minDateComp: {
              minDate: format(
                min,
                globalThis.sigv.appContext.envConfig.format.date
              ),
              orEqual,
              custMsg: custMsg,
              replaceMap: {},
            },
          }
        : null;

      return result;
    };
  }
}
